import { IconButton, Tooltip } from "@mui/material";
import { usePermissions } from "hooks/usePermissions";
import {
  FaCalendarAlt,
  FaClock,
  FaExternalLinkAlt,
  FaGraduationCap,
  FaMapMarkerAlt,
  FaPencilAlt,
  FaUniversity,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  });
};

const FactCell = ({ icon, label, value }) => (
  <div className="flex items-start gap-2">
    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-app-surface-muted text-app-text-muted">
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-[11px] font-medium uppercase tracking-wide text-app-text-muted">{label}</p>
      <p className="truncate text-sm font-semibold text-app-text">{value || "—"}</p>
    </div>
  </div>
);

const KeyValueRow = ({ label, value, valueClassName = "text-app-text" }) => (
  <div className="flex items-baseline justify-between gap-3 text-sm">
    <span className="text-app-text-muted">{label}</span>
    <span className={`font-semibold ${valueClassName}`}>{value}</span>
  </div>
);

const CourseCard = ({ course, onView, onEdit, canEdit }) => {
  const primaryOnshore = course?.onshoreFees?.[0];
  const enrollmentFee = formatCurrency(course?.enrollmentFee);
  const materialFee = formatCurrency(course?.materialFee);
  const onshorePerSem = formatCurrency(primaryOnshore?.feePerSem);
  const onshoreTotal = formatCurrency(primaryOnshore?.totalFees);
  const scholarship = primaryOnshore?.scholarship
    ? `${primaryOnshore.scholarship}%`
    : course?.maxScholarship
    ? `${course.maxScholarship}%`
    : null;
  const afterScholarship =
    primaryOnshore?.feePerSem && primaryOnshore?.scholarship
      ? formatCurrency(
          Number(primaryOnshore.feePerSem) -
            (Number(primaryOnshore.feePerSem) * Number(primaryOnshore.scholarship)) / 100
        )
      : null;

  const hasFeeInfo =
    enrollmentFee ||
    materialFee ||
    onshorePerSem ||
    onshoreTotal ||
    scholarship ||
    afterScholarship;

  return (
    <article
      onClick={onView}
      className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-xl border border-app-border bg-app-surface shadow-sm transition hover:-translate-y-0.5 hover:border-app-border hover:shadow-md"
    >
      <header className="flex items-start gap-3 border-b border-app-border px-5 py-4">
        <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-app-surface-hover text-primary">
          <FaGraduationCap />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold leading-snug text-app-text group-hover:text-sky-700">
            {course?.courseName || "Untitled course"}
          </h3>
          {course?.universityName && (
            <p className="mt-1 flex items-center gap-1.5 truncate text-sm text-app-text-secondary">
              <FaUniversity className="text-app-text-muted" />
              <span className="truncate">{course.universityName}</span>
            </p>
          )}
        </div>
        {canEdit && (
          <Tooltip title="Edit course">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              sx={{
                border: "1px solid",
                borderColor: "var(--app-border)",
                borderRadius: "8px",
                "&:hover": { backgroundColor: "var(--app-surface-alt)" },
              }}
            >
              <FaPencilAlt className="text-sm text-app-text-muted" />
            </IconButton>
          </Tooltip>
        )}
      </header>

      <div className="flex flex-1 flex-col gap-4 px-5 py-4">
        <div className="grid grid-cols-2 gap-3">
          <FactCell
            icon={<FaClock className="text-xs" />}
            label="Duration"
            value={course?.durationInWeeks ? `${course.durationInWeeks} wks` : null}
          />
          <FactCell
            icon={<FaCalendarAlt className="text-xs" />}
            label="Semesters"
            value={course?.noOfSem || null}
          />
          <FactCell
            icon={<FaMapMarkerAlt className="text-xs" />}
            label="State"
            value={primaryOnshore?.state || null}
          />
          <FactCell
            icon={<FaMapMarkerAlt className="text-xs" />}
            label="Program Level"
            value={course?.programLevel?.join(", ") || null}
          />
        </div>

        {hasFeeInfo && (
          <div className="rounded-lg border border-app-border bg-app-surface-muted/70 px-4 py-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-app-text-secondary">
                Fee structure
              </p>
              {scholarship && (
                <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                  {scholarship} scholarship
                </span>
              )}
            </div>
            <div className="space-y-1.5">
              {onshorePerSem && <KeyValueRow label="Per semester" value={onshorePerSem} />}
              {afterScholarship && (
                <KeyValueRow
                  label="After scholarship"
                  value={afterScholarship}
                  valueClassName="text-emerald-700"
                />
              )}
              {onshoreTotal && <KeyValueRow label="Total fees" value={onshoreTotal} />}
              {enrollmentFee && <KeyValueRow label="Enrolment" value={enrollmentFee} />}
              {materialFee && <KeyValueRow label="Material" value={materialFee} />}
            </div>
            {course?.onshoreFees?.length > 1 && (
              <p className="mt-2 text-xs text-app-text-muted">
                + {course.onshoreFees.length - 1} more state
                {course.onshoreFees.length > 2 ? "s" : ""} available
              </p>
            )}
          </div>
        )}

        {course?.intakes?.length > 0 && (
          <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-app-text-muted">
              Intakes
            </p>
            <div className="flex flex-wrap gap-1.5">
              {course.intakes.map((intake, i) => (
                <span
                  key={`${intake}-${i}`}
                  className="inline-flex items-center rounded-md border border-app-border bg-app-surface px-2 py-0.5 text-xs font-medium text-app-text"
                >
                  {intake}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <footer className="flex items-center justify-between gap-3 border-t border-app-border bg-app-surface-muted px-5 py-3">
        <div className="flex items-center gap-2 text-xs text-app-text-muted">
          <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
          Active
          {course?.offerings?.length > 1 && (
            <span className="ml-2 rounded-md bg-slate-200/70 px-1.5 py-0.5 text-[11px] font-semibold text-app-text">
              {course.offerings.length} offerings
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {course?.websiteLink && (
            <Tooltip title="Visit course website">
              <a
                href={course.websiteLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-app-border bg-app-surface text-app-text-muted hover:bg-app-surface-muted hover:text-app-text"
              >
                <FaExternalLinkAlt className="text-xs" />
              </a>
            </Tooltip>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onView();
            }}
            className="inline-flex items-center gap-1.5 rounded-md bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-sky-700"
          >
            View details
          </button>
        </div>
      </footer>
    </article>
  );
};

const CourseGrid = ({ courses }) => {
  const navigate = useNavigate();
  const { isSuperAdmin } = usePermissions();

  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
      {courses?.map((course, index) => (
        <CourseCard
          key={course?.courseId || index}
          course={course}
          canEdit={isSuperAdmin}
          onView={() => {
            if (!course?.uniId || !course?.courseId) return;
            navigate(`/admin/university/${course.uniId}/course/edit/${course.courseId}`, {
              state: { item: course, from: "course" },
            });
          }}
          onEdit={() => {
            navigate(`/admin/university/${course?.uniId}/course/edit/${course?.courseId}`, {
              state: { item: course, from: "course" },
            });
          }}
        />
      ))}
    </div>
  );
};

export default CourseGrid;
